import { computed, inject, linkedSignal, signal } from '@angular/core';
import { FEATURE_FLAG, FeatureFlagService } from '@quipex/core/config';
import type { UserType } from '@quipex/shared/contracts';
import { QuippyPackActions } from '../../runtime/pack-actions/pack-actions';
import {
    getQuippyOutcomeSuggestions,
    type QuippySuggestedQuestion,
} from '../../suggestions';

export type QuippyOutcome = 'ask' | 'find' | 'pack';

export function injectQuippyOutcomeSelection(context: {
    startWithPack: () => boolean;
    isAdmin: () => boolean;
    identity: () => string;
    userType: () => UserType | undefined;
    scope: () => { kind: 'building' | 'portfolio' };
    questions: () => readonly QuippySuggestedQuestion[];
}) {
    const actions = inject(QuippyPackActions, { optional: true });
    const packFlag = inject(FeatureFlagService).flagEnabled(
        FEATURE_FLAG.QuippyPackActions
    );
    const packAvailable = computed(
        () =>
            packFlag() &&
            actions?.enabled() === true &&
            context.scope().kind === 'building' &&
            !context.isAdmin()
    );
    const identity = computed(
        () => `${context.identity()}:${context.userType() ?? 'other'}`
    );
    const selected = linkedSignal({
        source: () => ({
            identity: identity(),
            packAvailable: packAvailable(),
            startWithPack: context.startWithPack(),
        }),
        computation: (source, previous): QuippyOutcome =>
            (!previous || previous.source.identity === source.identity) &&
            source.startWithPack &&
            source.packAvailable
                ? 'pack'
                : 'ask',
    });
    const activation = signal(0);
    const activate = (outcome: QuippyOutcome) => {
        selected.set(outcome);
        activation.update((value) => value + 1);
    };
    const questions = computed(() => {
        const outcome = selected();
        return outcome === 'ask'
            ? context.questions()
            : getQuippyOutcomeSuggestions(
                  outcome,
                  context.scope().kind,
                  context.userType()
              );
    });
    const initialCategory = computed(() => {
        return context
            .questions()
            .some(({ category }) => category === 'Understand it')
            ? ('Understand it' as const)
            : (context.questions()[0]?.category ?? null);
    });
    const showCategories = computed(() => selected() === 'ask');
    return {
        selected,
        activation,
        activate,
        questions,
        packAvailable,
        showCategories,
        initialCategory,
    };
}
